import { InfoCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, theme } from 'antd';
import type { EventBus, PDFFindController, PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';
import type React from 'react';
import { useState } from 'react';
import { DocumentPropertiesModal } from './DocumentPropertiesModal';
import { FindControls } from './FindControls';
import { PageControls } from './PageControls';
import { ZoomControls } from './ZoomControls';

/**
 * Which toolbar groups to show. Everything is on by default; a caller embedding the
 * viewer in a small preview can drop what does not earn its space there.
 */
export interface PdfToolbarVisibility {
  /** Previous/next buttons and the editable page number. */
  pages?: boolean;
  /** Zoom buttons and the zoom dropdown. */
  zoom?: boolean;
  /** Search field with match count and next/previous. */
  search?: boolean;
  /** Info button opening the document properties dialog. */
  properties?: boolean;
}

interface Props {
  viewer: PDFViewer | null;
  eventBus: EventBus | null;
  findController: PDFFindController | null;
  visibility: PdfToolbarVisibility;
}

export const PdfToolbar: React.FC<Props> = ({ viewer, eventBus, findController, visibility }) => {
  const { token } = theme.useToken();
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  const showPages = visibility.pages !== false;
  const showZoom = visibility.zoom !== false;
  const showSearch = visibility.search !== false;
  const showProperties = visibility.properties !== false;

  // Every control in here has a fixed width, so nothing can resize while the viewer is
  // used and `space-between` cannot shift a neighbouring group — the equal-zone layout
  // this replaced was only needed while the match counter grew and shrank. Groups keep
  // their natural width, so a wrapped row starts at the left edge instead of being
  // stretched to full width and pushed right.
  const groupStyle = css`
    display: flex;
    align-items: center;
    gap: ${token.paddingXS}px;
  `;

  const toolbarStyle = css`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: ${token.paddingXS}px ${token.padding}px;
    padding: ${token.paddingXS}px 0;
    background: ${token.colorBgContainer};
    border-bottom: 1px solid ${token.colorBorderSecondary};
  `;

  // Nothing left to show: render no bar at all rather than an empty strip with a border.
  if (!showPages && !showZoom && !showSearch && !showProperties) {
    return null;
  }

  return (
    <div className={toolbarStyle}>
      {showPages && (
        <div className={groupStyle}>
          <PageControls viewer={viewer} eventBus={eventBus} />
        </div>
      )}
      {showZoom && (
        <div className={groupStyle}>
          <ZoomControls viewer={viewer} eventBus={eventBus} />
        </div>
      )}
      {(showSearch || showProperties) && (
        <div className={groupStyle}>
          {showSearch && <FindControls eventBus={eventBus} findController={findController} />}
          {showProperties && (
            <Button
              icon={<InfoCircleOutlined />}
              aria-label="Dokumenteigenschaften"
              title="Dokumenteigenschaften"
              disabled={!viewer?.pdfDocument}
              onClick={() => setPropertiesOpen(true)}
            />
          )}
        </div>
      )}
      {showProperties && (
        <DocumentPropertiesModal
          open={propertiesOpen}
          pdfDocument={viewer?.pdfDocument ?? null}
          currentPage={viewer?.currentPageNumber ?? 1}
          pagesRotation={viewer?.pagesRotation ?? 0}
          onClose={() => setPropertiesOpen(false)}
        />
      )}
    </div>
  );
};
